import { observer } from 'mobx-react';
import { Box, CircularProgress, IconButton, Typography } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import { INFO_600, SUCCESS_GREEN } from './trackRecordV2.styles';

type PendingAgentSearch = {
  id: string;
  label: string;
  flow: 'ENTITY_LOOKUP' | 'PROPERTY_LOOKUP' | 'UPLOAD';
  status: 'pending' | 'done';
  resultCount: number | null;
};

const describeDoneSearch = (search: PendingAgentSearch) => {
  const count = search.resultCount ?? 0;
  if (count === 0) {
    return {
      title: `"${search.label}" finished — no new properties found.`,
      subtext: null as string | null,
    };
  }
  const noun = count === 1 ? 'property' : 'properties';
  return {
    title: `"${search.label}" finished — ${count} ${noun} added to Properties.`,
    subtext: null as string | null,
  };
};

export const AgentSearchBanner = observer(({
  classes,
  pendingSearches,
  onNavigate,
  onDismiss,
}: {
  classes: Record<string, string>;
  pendingSearches: PendingAgentSearch[];
  onNavigate?: (item: 'properties' | 'entities') => void;
  onDismiss?: (id: string) => void;
}) => {
  const activeSearches = (pendingSearches ?? []).filter((search) => search.status === 'pending');
  const doneSearches = (pendingSearches ?? []).filter((search) => search.status === 'done');

  if (activeSearches.length === 0 && doneSearches.length === 0) return null;

  return (
    <>
      {activeSearches.length > 0 && (
        <Box className={classes.agentSearchBanner}>
          <CircularProgress size={20} thickness={4} style={{ color: INFO_600, flexShrink: 0 }} />
          <Box style={{ flex: 1 }}>
            <Typography className={classes.agentSearchBannerTitle}>
              {`${activeSearches[0].flow === 'UPLOAD' ? 'Processing' : 'Searching public records for'} "${activeSearches[0].label}"${
                activeSearches.length > 1 ? ` and ${activeSearches.length - 1} other${activeSearches.length > 2 ? 's' : ''}` : ''
              }`}
            </Typography>
            <Typography className={classes.agentSearchBannerSubtext}>
              Each takes 2-5 minutes. Keep searching in the meantime — results appear under Properties and Entities as they finish.
            </Typography>
          </Box>
          {/* Stops waiting on every running search; the agents keep running in the background. */}
          <IconButton
            size="small"
            aria-label="Stop waiting for search results"
            title="Close — the search keeps running in the background"
            style={{ flexShrink: 0 }}
            onClick={() => activeSearches.forEach((search) => onDismiss?.(search.id))}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      {doneSearches.map((search) => {
        const { title } = describeDoneSearch(search);
        return (
          <Box key={search.id} className={classes.agentSearchBannerDone}>
            <CheckIcon style={{ color: SUCCESS_GREEN, flexShrink: 0 }} />
            <Typography className={classes.agentSearchBannerDoneTitle}>{title}</Typography>
            <Box className={classes.agentSearchBannerDoneActions}>
              <button
                type="button"
                className={classes.bannerButtonOutline}
                onClick={() => {
                  onNavigate?.('entities');
                  onDismiss?.(search.id);
                }}
              >
                View Entities
              </button>
              <button
                type="button"
                className={classes.bannerButtonSolid}
                onClick={() => {
                  onNavigate?.('properties');
                  onDismiss?.(search.id);
                }}
              >
                View Properties
              </button>
              <IconButton size="small" onClick={() => onDismiss?.(search.id)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        );
      })}
    </>
  );
});
